import { EventEmitter, Inject, Injectable } from '@angular/core';
import { PROJECT_PROVIDER } from '../../../core';

@Injectable({
	providedIn: 'root'
})
export class ProjectAttachmentService {
	// Event wenn Attachments geändert wurden
	attachmentsChanged = new EventEmitter<void>();

	// Akzeptierte MIME-Types
	private readonly ACCEPTED_TYPES = new Set([
		'image/jpeg',
		'image/png',
		'image/bmp',
		'image/tiff',
		'application/pdf'
	]);

	// Akzeptierte Dateiendungen
	private readonly ACCEPTED_EXTENSIONS = new Set([
		'.jpg', '.jpeg', '.png', '.bmp', '.tif', '.tiff', '.pdf'
	]);

	// Maximale Dateigröße (10 MB)
	private readonly MAX_FILE_SIZE = 10 * 1024 * 1024;

	constructor(@Inject(PROJECT_PROVIDER) private ps: any) {}

	/**
	 * Prüft ob ein Dateityp erlaubt ist.
	 */
	isFileTypeAllowed(file: File): boolean {
		// MIME-Type prüfen
		if (this.ACCEPTED_TYPES.has(file.type)) {
			return true;
		}

		// Fallback: Extension prüfen (manche Browser setzen keinen MIME-Type)
		const ext = '.' + file.name.split('.').pop()?.toLowerCase();
		return this.ACCEPTED_EXTENSIONS.has(ext);
	}

	/**
	 * Gibt eine lesbare Fehlermeldung für ungültige Dateien zurück.
	 */
	getValidationError(file: File): string | null {
		if (!this.isFileTypeAllowed(file)) {
			return `Dateityp nicht unterstützt: ${file.name}\n\nErlaubte Formate: JPG, PNG, BMP, TIF, PDF`;
		}
		if (file.size > this.MAX_FILE_SIZE) {
			const sizeMB = (file.size / (1024 * 1024)).toFixed(1);
			return `Datei zu groß: ${sizeMB} MB\n\nMaximal erlaubt: 10 MB`;
		}
		return null;
	}

	/**
	 * Lädt ein Attachment hoch.
	 */
	async uploadAttachment(projectId: string, file: File): Promise<boolean> {
		// Validierung
		const error = this.getValidationError(file);
		if (error) {
			alert(error);
			return false;
		}

		// Eindeutigen Dateinamen generieren falls nötig
		const filename = await this.getUniqueFilename(projectId, file.name);

		// Attachment hinzufügen
		const success = await this.ps.addProjectAttachment(projectId, filename, file, file.type);
		if (!success) {
			alert('Fehler beim Hochladen der Datei.');
			return false;
		}

		// Metadaten aktualisieren
		const meta = await this.getAttachmentsMeta(projectId);
		meta.push({
			name: filename,
			contentType: file.type,
			size: file.size,
			uploadedAt: new Date().toISOString()
		});
		await this.ps.updateAttachmentsMeta(projectId, meta);

		this.attachmentsChanged.emit();
		return true;
	}

	/**
	 * Gibt die Metadaten aller Attachments zurück.
	 */
	async getAttachmentsMeta(projectId: string): Promise<any[]> {
		try {
			const doc = await this.ps.getOne(projectId) as any;
			return doc?.attachmentsMeta ?? [];
		} catch {
			return [];
		}
	}

	/**
	 * Lädt ein Attachment (lazy).
	 */
	async getAttachment(projectId: string, filename: string): Promise<Blob | null> {
		return await this.ps.getProjectAttachment(projectId, filename);
	}

	/**
	 * Löscht ein Attachment.
	 */
	async deleteAttachment(projectId: string, filename: string): Promise<boolean> {
		// Attachment entfernen
		const success = await this.ps.removeProjectAttachment(projectId, filename);
		if (!success) {
			return false;
		}

		// Metadaten aktualisieren
		const meta = await this.getAttachmentsMeta(projectId);
		const filtered = meta.filter(m => m.name !== filename);
		await this.ps.updateAttachmentsMeta(projectId, filtered);

		this.attachmentsChanged.emit();
		return true;
	}

	/**
	 * Generiert einen eindeutigen Dateinamen falls nötig.
	 */
	private async getUniqueFilename(projectId: string, originalName: string): Promise<string> {
		const meta = await this.getAttachmentsMeta(projectId);
		const existingNames = new Set(meta.map(m => m.name));

		if (!existingNames.has(originalName)) {
			return originalName;
		}

		// Zahl anhängen
		const dotIndex = originalName.lastIndexOf('.');
		const baseName = dotIndex > 0 ? originalName.substring(0, dotIndex) : originalName;
		const ext = dotIndex > 0 ? originalName.substring(dotIndex) : '';

		let counter = 1;
		let newName = `${baseName}_${counter}${ext}`;
		while (existingNames.has(newName)) {
			counter++;
			newName = `${baseName}_${counter}${ext}`;
		}

		return newName;
	}

	/**
	 * Generiert ein Thumbnail für ein Bild.
	 * @param blob Die Bilddaten
	 * @param maxSize Maximale Breite/Höhe in Pixeln
	 * @returns Data-URL des Thumbnails
	 */
	async generateThumbnail(blob: Blob, maxSize = 100): Promise<string> {
		return new Promise((resolve, reject) => {
			const img = new Image();
			const url = URL.createObjectURL(blob);

			img.onload = () => {
				URL.revokeObjectURL(url);

				// Skalierung berechnen
				let width = img.width;
				let height = img.height;
				if (width > height) {
					if (width > maxSize) {
						height = Math.round((height * maxSize) / width);
						width = maxSize;
					}
				} else {
					if (height > maxSize) {
						width = Math.round((width * maxSize) / height);
						height = maxSize;
					}
				}

				// Canvas erstellen und zeichnen
				const canvas = document.createElement('canvas');
				canvas.width = width;
				canvas.height = height;
				const ctx = canvas.getContext('2d')!;
				ctx.drawImage(img, 0, 0, width, height);

				// Als Data-URL zurückgeben
				resolve(canvas.toDataURL('image/jpeg', 0.7));
			};

			img.onerror = () => {
				URL.revokeObjectURL(url);
				reject(new Error('Fehler beim Laden des Bildes'));
			};

			img.src = url;
		});
	}

	/**
	 * Prüft ob ein MIME-Type ein Bild ist.
	 */
	isImage(contentType: string): boolean {
		return contentType.startsWith('image/');
	}

	/**
	 * Prüft ob ein MIME-Type ein PDF ist.
	 */
	isPdf(contentType: string): boolean {
		return contentType === 'application/pdf';
	}
}
